import { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, HandshakeIcon, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useSubLock } from "@/lib/subLock";

export const fmtTL = (n) => `${Number(n || 0).toLocaleString("tr-TR")} TL`;
export const priceLabel = (r) => `${fmtTL(r.yh_price_tl)} · ${r.yh_pricing_method === "negotiable" ? "Pazarlığa açık" : "Sabit fiyat"}`;

export const YhM3Badge = ({ id }) => (
  <Badge className="bg-emerald-700 text-white hover:bg-emerald-700 text-[10px]" data-testid={`tp-m3-badge-${id}`}>Doğrudan Müşteri İletişimi</Badge>
);

export const YhCallButton = ({ phone, testid }) => (
  <a href={`tel:${phone}`} data-testid={testid}
    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-sm font-semibold">
    <Phone className="w-4 h-4" />Müşteriyi Ara
  </a>
);

const ClaimState = ({ r }) => {
  if (r.yh_my_claim_status === "pending") return <Badge className="bg-amber-500 text-white hover:bg-amber-500" data-testid={`tp-m3-claim-pending-${r.id}`}>Yönetici onayı bekleniyor</Badge>;
  if (r.yh_claim_cooldown_until && r.yh_claim_cooldown_until > new Date().toISOString()) {
    return <Badge variant="destructive" data-testid={`tp-m3-cooldown-${r.id}`}>Başvurunuz reddedildi · {new Date(r.yh_claim_cooldown_until).toLocaleString("tr-TR")} sonrası tekrar başvurabilirsiniz</Badge>;
  }
  return null;
};

export const YhM3PoolActions = ({ r, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const { tradeLocked, msg: lockMsg } = useSubLock();
  const c = r.yh_contact;
  const state = <ClaimState r={r} />;
  const claim = async () => {
    if (!window.confirm("Müşteriyle anlaştınız mı? 'İşi Aldım' bildirimi Yol Hattı yönetiminin onayına gönderilecek.")) return;
    setBusy(true);
    try { await api.post(`/transport/requests/${r.id}/claim`, { note: "" }); toast.success("Bildiriminiz yöneticiye iletildi, onay bekleniyor"); onChanged(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2" data-testid={`tp-m3-${r.id}`}>
      <div className="text-sm font-bold" data-testid={`tp-m3-price-${r.id}`}>{priceLabel(r)}</div>
      {c ? (
        <div className="rounded-xl bg-muted/50 p-2 text-xs space-y-1" data-testid={`tp-m3-contact-${r.id}`}>
          <div><b>{c.customer_name}</b></div>
          <div className="flex gap-1"><MapPin className="w-3 h-3 mt-0.5" />Alım: {c.pickup_address}</div>
          <div className="flex gap-1"><MapPin className="w-3 h-3 mt-0.5" />Teslim: {c.delivery_address}</div>
        </div>
      ) : <div className="text-xs text-muted-foreground">Müşteri bilgileri yalnızca onaylı işletmelere gösterilir.</div>}
      <div className="flex items-center gap-2 flex-wrap">
        {c?.phone && <YhCallButton phone={c.phone} testid={`tp-m3-call-${r.id}`} />}
        {c && !r.yh_my_claim_status && !(r.yh_claim_cooldown_until > new Date().toISOString()) && (
          <Button size="sm" onClick={claim} disabled={busy || tradeLocked} title={tradeLocked ? lockMsg : undefined} data-testid={`tp-m3-claim-${r.id}`} className="rounded-full bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <HandshakeIcon className="w-4 h-4 mr-1" />}İşi Aldım
          </Button>
        )}
        {state}
        {tradeLocked && c && !r.yh_my_claim_status && <span className="text-xs text-destructive w-full" data-testid={`tp-m3-lock-${r.id}`}>{lockMsg}</span>}
      </div>
    </div>
  );
};
